import Reveal from "./Reveal";
import { useLang } from "@/i18n";

const FBA_IMG =
  "https://images.unsplash.com/photo-1633174524827-db00a6b7bc74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxhbWF6b24lMjBmYmElMjBwYWNrYWdpbmclMjBwYXJjZWwlMjBkZWxpdmVyeSUyMHdvcmtlciUyMGZhc2hpb24lMjB0b3lzJTIwaG9tZSUyMGdvb2RzfGVufDB8fHx8MTc5MjAwMDI2Nnww&ixlib=rb-4.1.0&q=85";
const CONTAINER_IMG =
  "https://images.unsplash.com/photo-1613690399151-65ea69478674?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXBwaW5nJTIwcG9ydCUyMGFlaWFsJTIwY29udGFpbmVyc3xlbnwwfHx8fDE3ODY0NTEzMjN8MA&ixlib=rb-4.1.0&q=85";

function ImageCard({ img, title, text, testid, meta }) {
  return (
    <div
      data-testid={testid}
      className="group relative flex h-full min-h-[340px] flex-col justify-end overflow-hidden rounded-2xl border border-line"
    >
      <img
        src={img}
        alt={title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
      {meta && (
        <span className="font-mono absolute right-4 top-4 rounded-full border border-white/25 bg-obsidian/60 px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-paper backdrop-blur-sm">
          {meta}
        </span>
      )}
      <div className="relative p-7">
        <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-paper">{title}</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-paper/70">{text}</p>
      </div>
    </div>
  );
}

export default function Services() {
  const { t } = useLang();

  return (
    <section id="services" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <Reveal>
          <p className="font-mono flex items-center gap-3 text-[11px] font-bold tracking-[0.25em] text-obsidian/60">
            <span className="h-2 w-2 rotate-45 bg-brand" />
            {t.services.eyebrow}
          </p>
          <h2 className="font-display font-expanded mt-5 max-w-3xl text-3xl font-black uppercase tracking-tight text-obsidian sm:text-4xl lg:text-5xl">
            {t.services.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-7" delay={0.05}>
            <div
              data-testid="service-card-warehouse"
              className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl bg-obsidian p-8 text-paper transition-transform duration-300 hover:-translate-y-1.5 sm:p-10 lg:min-h-[360px]"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rotate-12 border border-paper/10" />
              <div>
                <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-brand">{t.services.warehouse.eyebrow}</p>
                <h3 className="font-display font-expanded mt-4 text-2xl font-black uppercase tracking-tight sm:text-3xl">
                  {t.services.warehouse.title}
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-paper/60 sm:text-base">
                  {t.services.warehouse.text}
                </p>
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-paper/15 bg-paper/5 px-5 py-4">
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-paper sm:text-sm">
                  {t.services.warehouse.address}
                </span>
                <span className="flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.2em] text-[#10B981]">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-[#10B981]" />
                  {t.services.warehouse.status}
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.12}>
            <ImageCard
              img={FBA_IMG}
              title={t.services.fba.title}
              text={t.services.fba.text}
              testid="service-card-fba"
              meta="AMAZON"
            />
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.05}>
            <ImageCard
              img={CONTAINER_IMG}
              title={t.services.direct.title}
              text={t.services.direct.text}
              testid="service-card-direct"
              meta={t.services.direct.route}
            />
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.12}>
            <div
              data-testid="service-card-ops"
              className="group flex h-full flex-col justify-between rounded-2xl border border-line bg-white p-8 transition-transform duration-300 hover:-translate-y-1.5 sm:p-10 lg:min-h-[360px]"
            >
              <div>
                <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-brand">{t.services.ops.eyebrow}</p>
                <h3 className="font-display font-expanded mt-4 text-2xl font-black uppercase tracking-tight text-obsidian sm:text-3xl">
                  {t.services.ops.title}
                </h3>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-obsidian/60 sm:text-base">
                  {t.services.ops.text}
                </p>
              </div>
              <ul className="mt-10 flex flex-wrap gap-2.5">
                {t.services.ops.items.map((item) => (
                  <li
                    key={item}
                    className="font-mono rounded-full border border-obsidian/15 px-4 py-2 text-[10px] font-bold tracking-[0.2em] text-obsidian/70 transition-colors duration-300 group-hover:border-brand group-hover:text-obsidian"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-12" delay={0.05}>
            <div
              data-testid="service-card-market"
              className="group flex flex-col justify-between gap-6 rounded-2xl bg-obsidian p-8 text-paper transition-transform duration-300 hover:-translate-y-1.5 sm:flex-row sm:items-center sm:p-10"
            >
              <div>
                <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-brand">{t.services.market.eyebrow}</p>
                <h3 className="font-display font-expanded mt-4 text-2xl font-black uppercase tracking-tight sm:text-3xl">
                  {t.services.market.title}
                </h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-paper/60 sm:text-base">
                  {t.services.market.text}
                </p>
              </div>
              <span className="font-mono shrink-0 rounded-full border border-brand px-5 py-3 text-[10px] font-bold tracking-[0.2em] text-brand">
                {t.services.market.route}
              </span>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-12" delay={0.05}>
            <div
              data-testid="service-card-categories"
              className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-line bg-white p-8 sm:flex-row sm:items-center sm:p-10"
            >
              <div>
                <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-obsidian/50">
                  {t.services.categories.label}
                </p>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {t.marquee.items.map((c) => (
                    <span
                      key={c}
                      className="font-display rounded-full bg-obsidian px-4 py-2 text-xs font-bold uppercase tracking-wide text-paper"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <span className="font-display shrink-0 rounded-full border-2 border-dashed border-brand px-5 py-3 text-xs font-black uppercase tracking-wide text-brand">
                {t.services.categories.excluded}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
